import {
  Box,
  Flex,
  IconButton,
  Input,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
  VisuallyHidden,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';
import { ReactComponent as LockIcon } from 'assets/icons/lock.svg';
import { ReactComponent as LockedIcon } from 'assets/icons/locked.svg';
import { ReactComponent as HorizontalMirrorIcon } from 'assets/icons/mirror_horizontal.svg';
import { ReactComponent as VerticalMirrorIcon } from 'assets/icons/mirror_vertical.svg';
import { ReactComponent as TransformIcon } from 'assets/icons/toolbar/transform.svg';
import { Direction } from 'constants/bounding-box';
import { Code } from 'constants/keyboard';
import { Mixpanel, TRANSFORM_TRACK } from 'libs/third-party/Mixpanel';
import { InputError } from 'modules/common/components/Input';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetType } from 'types/widget.types';
import { BasicShapeWidgetData } from 'widgets/BasicShapeWidget/BasicShapeWidget.types';
import { isSquareFrame } from 'widgets/ImageWidget/Frame';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { useEditor, useWidget } from 'widgets/sdk';

import { Resize } from '../../../modules/Editor/components/BoundingBox/useBoundingBox/useBoundingBox.types';
import { LIST_MIRROR_DISABLED } from './TransformMenu.config';

const UNIT_SUFFIX: Record<string, string> = {
  widthPx: 'px',
  heightPx: 'px',
  rotateDeg: '°',
};

export const TransformMenu = (): ReactElement | null => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false });
  const { isOpen, onClose, onToggle } = useDisclosure();

  const resizeObjectLabel = t('resizeObject');

  return (
    <Popover isOpen={isOpen} placement='bottom-start' onClose={onClose} closeOnBlur closeOnEsc isLazy>
      <Tooltip hasArrow placement='bottom' label={resizeObjectLabel} bg='black'>
        <Box>
          <PopoverTrigger>
            <IconButton
              aria-label={resizeObjectLabel}
              icon={<TransformIcon />}
              size={'sm'}
              variant={'ghost-with-active'}
              onClick={onToggle}
              isActive={isOpen}
            />
          </PopoverTrigger>
        </Box>
      </Tooltip>
      <PopoverContent w='fit-content' minW='253px'>
        <VisuallyHidden>
          <PopoverHeader>{resizeObjectLabel}</PopoverHeader>
        </VisuallyHidden>
        <SingleWidget />
      </PopoverContent>
    </Popover>
  );
};

const SingleWidget = () => {
  const {
    widthPx,
    heightPx,
    widgetId,
    rotateDeg,
    updateWidget,
    mirror = {},
    isAspectRatioLocked,
    isMirrored = false,
    type,
  } = useWidget<
    BasicShapeWidgetData & {
      isMirrored?: boolean;
    }
  >();
  const { frame } = useWidget<ImageWidgetData>();

  const widgetType = getWidgetTypeFromId(widgetId);
  const mirrorDisabled = LIST_MIRROR_DISABLED.includes(widgetType);
  const onlyLockedRatio = [WidgetType.Icon, WidgetType.ProgressChart].includes(widgetType) || isSquareFrame(frame);
  const [sizeError, setSizeError] = useBoolean(false);

  const [defaultWidgetData, setDefaultWidgetData] = useState(() => {
    const aspectRatioLocked = onlyLockedRatio ? true : isAspectRatioLocked;

    return {
      widthPx: Math.round(widthPx),
      heightPx: Math.round(heightPx),
      rotateDeg: Math.round(rotateDeg) || 0,
      isAspectRatioLocked: aspectRatioLocked,
      widthPxDisplay: Math.round(widthPx) + UNIT_SUFFIX.widthPx,
      heightPxDisplay: Math.round(heightPx) + UNIT_SUFFIX.heightPx,
      rotateDegDisplay: (Math.round(rotateDeg) || 0) + UNIT_SUFFIX.rotateDeg,
    };
  });

  const { boundingBox } = useEditor();

  useEffect(() => {
    setDefaultWidgetData((prevData) => ({
      ...prevData,
      widthPx: Math.round(widthPx),
      heightPx: Math.round(heightPx),
      rotateDeg: Math.round(rotateDeg),
      rotateDegDisplay: Math.round(rotateDeg) + UNIT_SUFFIX.rotateDeg,
      widthPxDisplay: Math.round(widthPx) + UNIT_SUFFIX.widthPx,
      heightPxDisplay: Math.round(heightPx) + UNIT_SUFFIX.heightPx,
    }));
  }, [widthPx, heightPx, rotateDeg]);

  const updateWidgetState = useCallback(
    (newData: Partial<typeof defaultWidgetData>) => {
      const updatedData = { ...defaultWidgetData, ...newData };

      if (updatedData.widthPx && updatedData.heightPx) {
        const resizePayload: Resize = {
          width: updatedData.widthPx,
          height: updatedData.heightPx,
          keepRatio: updatedData.isAspectRatioLocked,
        };

        // @ts-ignore to check if the widget is statchart icon
        if (widgetType === WidgetType.Icon && type === 'Grid') {
          resizePayload.deltaWidth = updatedData.widthPx - widthPx;
          resizePayload.deltaHeight = updatedData.heightPx - heightPx;
          resizePayload.side = Direction.EAST;
        }

        if (widgetType === WidgetType.Image && !resizePayload.keepRatio) {
          if (widthPx !== updatedData.widthPx && heightPx !== updatedData.heightPx) {
            resizePayload.side = Direction.SOUTH_EAST;
          } else if (widthPx !== updatedData.widthPx) {
            resizePayload.side = Direction.EAST;
          } else if (heightPx !== updatedData.heightPx) {
            resizePayload.side = Direction.SOUTH;
          }
        }

        boundingBox.resize(resizePayload);
        const rotateValue = updatedData.rotateDeg - rotateDeg;

        updateWidget({
          isAspectRatioLocked: updatedData.isAspectRatioLocked,
        });

        boundingBox.rotate({
          deltaRotate: rotateValue,
        });

        // Use requestAnimationFrame to ensure the bounding box update occurs after the browser has
        // finished processing the resize and widget update operations. This helps to avoid potential
        // layout thrashing and ensures the most up-to-date dimensions are used for the bounding box.
        requestAnimationFrame(() => {
          boundingBox.updateRect();
        });

        const transformedProperties = [];

        if (updatedData.rotateDeg !== rotateDeg) {
          transformedProperties.push('Rotate');
        }

        if (updatedData.widthPx !== widthPx || updatedData.heightPx !== heightPx) {
          transformedProperties.push('Resize');
        }

        Mixpanel.track(TRANSFORM_TRACK, { styling_property: transformedProperties.join(', ') });

        // reset transformedProperties value after tracking
        transformedProperties.length = 0;
      }
    },
    [boundingBox, updateWidget, defaultWidgetData, widgetType, type, widthPx, heightPx, rotateDeg],
  );

  const { t } = useTranslation('editor_toolbar', { useSuspense: false });
  const { t: tShapesToolbar } = useTranslation('editor_widget_toolbar', {
    useSuspense: false,
    keyPrefix: 'shapesToolbar',
  });

  const lockRatioLabel = t('lockRatio');
  const unlockRatioLabel = t('unlockRatio');
  const ratioLabel = defaultWidgetData.isAspectRatioLocked ? unlockRatioLabel : lockRatioLabel;
  const verticalLabel = tShapesToolbar('mirror.flipVertical');
  const horizontalLabel = tShapesToolbar('mirror.flipHorizontal');

  const handleInputChange =
    (field: keyof typeof defaultWidgetData, isUpdateWidget: boolean = true) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value === '' || e.target.value === UNIT_SUFFIX[field] ? 0 : parseFloat(e.target.value);
      value = isNaN(value) ? 0 : value;

      if (isUpdateWidget) {
        updateWidgetState({ [field]: value });
      } else {
        setDefaultWidgetData((prevData) => ({
          ...prevData,
          [field]: value,
          ...(field !== 'isAspectRatioLocked' ? { [`${field}Display`]: value } : {}),
        }));
      }
    };

  const handleMirror = (direction: 'horizontal' | 'vertical') => {
    Mixpanel.track(TRANSFORM_TRACK, { styling_property: 'Flip' });

    if (widgetType === WidgetType.Icon) {
      updateWidget({ isMirrored: !isMirrored });
      return;
    }

    if (direction === 'vertical') {
      updateWidget({ mirror: { isVertical: !mirror.isVertical } });
      return;
    }

    updateWidget({ mirror: { isHorizontal: !mirror.isHorizontal } });
  };

  const handleLockRatioToggle = () => {
    updateWidgetState({ isAspectRatioLocked: !defaultWidgetData.isAspectRatioLocked });
    setDefaultWidgetData((prevData) => ({
      ...prevData,
      isAspectRatioLocked: !prevData.isAspectRatioLocked,
    }));
  };

  const handleOnFocus = (field: keyof typeof defaultWidgetData) => (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    setDefaultWidgetData((prevData) => ({
      ...prevData,
      [`${field}Display`]: String(prevData[field]),
    }));
  };

  const handleOnBlur = (field: keyof typeof defaultWidgetData) => (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    setDefaultWidgetData((prevData) => ({
      ...prevData,
      [`${field}Display`]: prevData[field] + UNIT_SUFFIX[field],
    }));
  };

  const handleKeyDown = (field: keyof typeof defaultWidgetData) => (event: React.KeyboardEvent) => {
    if (event.key === Code.Enter) {
      event.stopPropagation();

      if (
        (field === 'widthPx' && defaultWidgetData.widthPx <= 0) ||
        (field === 'heightPx' && defaultWidgetData.heightPx <= 0)
      ) {
        setSizeError.on();
      } else {
        setSizeError.off();
      }

      handleInputChange(field)(event as any);

      const inputElement = document.getElementById(field) as HTMLInputElement;
      if (inputElement) inputElement.blur();
    }
  };

  return (
    <Flex gap='4' flexDirection='column' padding='4'>
      <Flex gap='10px'>
        <Box>
          <Text fontSize='xs' fontWeight='normal' mb='1'>
            {t('width')}
          </Text>
          <Input
            maxW='88px'
            h='28px'
            px='8px'
            placeholder='0px'
            id='widthPx'
            value={defaultWidgetData.widthPxDisplay}
            onChange={handleInputChange('widthPx', false)}
            onBlur={handleOnBlur('widthPx')}
            onFocus={handleOnFocus('widthPx')}
            onKeyDown={handleKeyDown('widthPx')}
          />
        </Box>
        <Box mt='auto'>
          <Tooltip hasArrow label={ratioLabel} bg='black' isDisabled={widgetType === WidgetType.Icon}>
            <IconButton
              aria-label={ratioLabel}
              icon={
                defaultWidgetData.isAspectRatioLocked ? (
                  <LockedIcon width='20px' height='20px' />
                ) : (
                  <LockIcon width='20px' height='20px' />
                )
              }
              variant='ghost-with-active'
              w='28px'
              h='28px'
              minW='28px'
              onClick={handleLockRatioToggle}
              isActive={defaultWidgetData.isAspectRatioLocked && !onlyLockedRatio}
              _disabled={{
                bg: 'transparent',
                color: '#95959A',
                cursor: 'default',
              }}
              disabled={onlyLockedRatio}
            />
          </Tooltip>
        </Box>
        <Box>
          <Text fontSize='xs' mb='1'>
            {t('height')}
          </Text>
          <Input
            maxW='88px'
            h='28px'
            px='8px'
            placeholder='0px'
            id='heightPx'
            value={defaultWidgetData.heightPxDisplay}
            onChange={handleInputChange('heightPx', false)}
            onBlur={handleOnBlur('heightPx')}
            onFocus={handleOnFocus('heightPx')}
            onKeyDown={handleKeyDown('heightPx')}
          />
        </Box>
      </Flex>
      {sizeError && (
        <Box>
          <InputError id='invalid-size'>{t('invalid_size')}</InputError>
        </Box>
      )}
      <Flex gap='10px'>
        <Box>
          <Text fontSize='xs' mb='1'>
            {t('rotate')}
          </Text>
          <Input
            maxW='88px'
            h='28px'
            px='8px'
            placeholder='0°'
            id='rotateDeg'
            value={defaultWidgetData.rotateDegDisplay}
            onChange={handleInputChange('rotateDeg', false)}
            onBlur={handleOnBlur('rotateDeg')}
            onFocus={handleOnFocus('rotateDeg')}
            onKeyDown={handleKeyDown('rotateDeg')}
          />
        </Box>
        {!mirrorDisabled && (
          <Box>
            <Text fontSize='xs' mb='1'>
              {t('flip')}
            </Text>
            <Flex gap='2'>
              <Tooltip hasArrow label={horizontalLabel} bg='black'>
                <IconButton
                  aria-label={horizontalLabel}
                  icon={<HorizontalMirrorIcon width='20px' height='20px' />}
                  variant='ghost'
                  w='28px'
                  h='28px'
                  minW='28px'
                  onClick={() => handleMirror('horizontal')}
                />
              </Tooltip>
              {widgetType !== WidgetType.Icon && (
                <Tooltip hasArrow label={verticalLabel} bg='black'>
                  <IconButton
                    aria-label={verticalLabel}
                    icon={<VerticalMirrorIcon width='20px' height='20px' />}
                    variant='ghost'
                    w='28px'
                    h='28px'
                    minW='28px'
                    onClick={() => handleMirror('vertical')}
                  />
                </Tooltip>
              )}
            </Flex>
          </Box>
        )}
      </Flex>
    </Flex>
  );
};
